
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Filter, MessageCircle, BarChart3 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface FeedbackDisplayProps {
  psychologistId: string;
  limit?: number;
  showFilters?: boolean;
}

interface FeedbackStats {
  average_rating: number;
  total_reviews: number;
  rating_distribution: {
    '5_stars': number;
    '4_stars': number;
    '3_stars': number;
    '2_stars': number;
    '1_star': number;
  };
  recent_reviews: Array<{
    id: string;
    rating: number;
    comment?: string;
    is_anonymous: boolean;
    created_at: string;
    user_name: string;
  }>;
}

const FeedbackDisplay: React.FC<FeedbackDisplayProps> = ({
  psychologistId,
  limit = 10,
  showFilters = false
}) => {
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('recent');

  // Fetch feedback statistics
  const { data: feedbackStats, isLoading, error } = useQuery({
    queryKey: ['psychologist-feedback-stats', psychologistId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_psychologist_feedback_stats', {
          p_psychologist_id: psychologistId
        });

      if (error) throw error;
      return data as unknown as FeedbackStats;
    },
    enabled: !!psychologistId
  });

  // Fetch detailed feedback for filtering and sorting
  const { data: detailedFeedback } = useQuery({
    queryKey: ['psychologist-feedback-detailed', psychologistId, filter, sortBy],
    queryFn: async () => {
      let query = supabase
        .from('feedback_with_user_info')
        .select('*')
        .eq('psychologist_id', psychologistId);

      // Apply rating filter
      if (filter === 'positive') {
        query = query.gte('rating', 4);
      } else if (filter === 'negative') {
        query = query.lte('rating', 2);
      }

      // Apply sorting
      if (sortBy === 'highest') {
        query = query.order('rating', { ascending: false });
      } else if (sortBy === 'lowest') {
        query = query.order('rating', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.limit(limit);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!psychologistId && showFilters
  });

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5'
    };

    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`${sizeClasses[size]} ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Star className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Carregando avaliações...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            Erro ao carregar avaliações
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!feedbackStats || feedbackStats.total_reviews === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Avaliações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Este psicólogo ainda não recebeu avaliações
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const reviewsToShow = showFilters && detailedFeedback ? detailedFeedback : feedbackStats.recent_reviews;

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className={`text-3xl font-bold ${getRatingColor(feedbackStats.average_rating)}`}>
                {feedbackStats.average_rating.toFixed(1)}
              </div>
              <div className="flex justify-center mt-1">
                {renderStars(Math.round(feedbackStats.average_rating), 'lg')}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Avaliação Média
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {feedbackStats.total_reviews}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Total de Avaliações
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {Math.round((feedbackStats.rating_distribution['5_stars'] + feedbackStats.rating_distribution['4_stars']) / feedbackStats.total_reviews * 100)}%
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Avaliações Positivas
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rating Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Distribuição das Avaliações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = feedbackStats.rating_distribution[`${stars}_star${stars === 1 ? '' : 's'}` as keyof typeof feedbackStats.rating_distribution];
              const percentage = feedbackStats.total_reviews > 0 ? (count / feedbackStats.total_reviews) * 100 : 0;
              
              return (
                <div key={stars} className="flex items-center gap-2">
                  <div className="flex items-center gap-1 w-20">
                    <span className="text-sm">{stars}</span>
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters (if enabled) */}
      {showFilters && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Avaliações Detalhadas
              </CardTitle>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="positive">Positivas (4-5★)</SelectItem>
                    <SelectItem value="negative">Negativas (1-2★)</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Mais Recentes</SelectItem>
                    <SelectItem value="highest">Maior Avaliação</SelectItem>
                    <SelectItem value="lowest">Menor Avaliação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Reviews List */}
      <Card>
        <CardContent className="pt-6">
          {reviewsToShow.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Nenhuma avaliação encontrada com os filtros aplicados
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviewsToShow.map((review) => (
                <div key={review.id} className="border-b pb-4 last:border-b-0">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        {getInitials(review.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {review.user_name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(parseISO(review.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </div>
                        </div>
                        <Badge variant={review.rating >= 4 ? 'default' : review.rating >= 3 ? 'secondary' : 'destructive'}>
                          {review.rating} ★
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {renderStars(review.rating)}
                      </div>
                      
                      {review.comment && (
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                          "{review.comment}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackDisplay;
